/**
 * PR Blocking Queue Component for SWRM-30
 *
 * Displays and manages workflows blocked by PR conditions including:
 * - Queue overview with priority sorting
 * - Blocking reasons and duration
 * - Management actions for blocked workflows
 * - Analytics and queue depth metrics
 * - Real-time updates for queue changes
 */

import React, { useState, useEffect } from "react";
import {
  Clock,
  AlertTriangle,
  TrendingUp,
  Filter,
  Search,
  RefreshCw,
  BarChart3,
  Users,
  GitPullRequest,
  CheckCircle,
} from "lucide-react";
import {
  useBlockingAnalytics,
  usePRActions,
} from "../../hooks/usePRMonitoring";
import { StatusBadge } from "../Common/StatusBadge";
import { MetricCard } from "../Common/MetricCard";

interface BlockedWorkflow {
  id: number;
  workflow_id: number;
  workflow_name?: string;
  reason: string;
  blocking_type: "dependency" | "resource" | "pr_status" | "manual";
  priority: number;
  blocked_since: string;
  duration_hours: number;
  dependencies?: string[];
  components?: string[];
}

interface PRBlockingQueueProps {
  className?: string;
}

export const PRBlockingQueue: React.FC<PRBlockingQueueProps> = ({
  className = "",
}) => {
  const { analytics, loading, error, refetch } = useBlockingAnalytics();
  const { overridePRBlocking, escalatePRIssue, actionLoading } = usePRActions();

  const [blockedWorkflows, setBlockedWorkflows] = useState<BlockedWorkflow[]>(
    []
  );
  const [filteredWorkflows, setFilteredWorkflows] = useState<BlockedWorkflow[]>(
    []
  );
  const [searchTerm, setSearchTerm] = useState("");
  const [filterType, setFilterType] = useState<string>("all");
  const [sortBy, setSortBy] = useState<"priority" | "duration" | "type">(
    "priority"
  );
  const [refreshing, setRefreshing] = useState(false);

  // Mock data for blocked workflows (in real implementation, this would come from API)
  useEffect(() => {
    const mockBlockedWorkflows: BlockedWorkflow[] = [
      {
        id: 1,
        workflow_id: 101,
        workflow_name: "Feature Authentication Enhancement",
        reason: "PR checks failing - unit tests not passing",
        blocking_type: "pr_status",
        priority: 3,
        blocked_since: "2025-09-25T06:30:00Z",
        duration_hours: 3.5,
        dependencies: [],
        components: ["auth", "api"],
      },
      {
        id: 2,
        workflow_id: 102,
        workflow_name: "Database Migration Workflow",
        reason: "Waiting for PR approval from code owners",
        blocking_type: "pr_status",
        priority: 2,
        blocked_since: "2025-09-25T04:15:00Z",
        duration_hours: 5.75,
        dependencies: ["101"],
        components: ["database", "migrations"],
      },
      {
        id: 3,
        workflow_id: 103,
        workflow_name: "Frontend Dashboard Updates",
        reason: "Merge conflicts detected in multiple files",
        blocking_type: "pr_status",
        priority: 1,
        blocked_since: "2025-09-24T18:45:00Z",
        duration_hours: 14.25,
        dependencies: [],
        components: ["frontend", "ui"],
      },
    ];
    setBlockedWorkflows(mockBlockedWorkflows);
  }, []);

  // Filter and sort workflows
  useEffect(() => {
    let filtered = blockedWorkflows;

    // Apply search filter
    if (searchTerm) {
      filtered = filtered.filter(
        (workflow) =>
          workflow.workflow_name
            ?.toLowerCase()
            .includes(searchTerm.toLowerCase()) ||
          workflow.reason.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    // Apply type filter
    if (filterType !== "all") {
      filtered = filtered.filter(
        (workflow) => workflow.blocking_type === filterType
      );
    }

    // Apply sorting
    filtered = filtered.sort((a, b) => {
      switch (sortBy) {
        case "priority":
          return b.priority - a.priority; // Higher priority first
        case "duration":
          return b.duration_hours - a.duration_hours; // Longer duration first
        case "type":
          return a.blocking_type.localeCompare(b.blocking_type);
        default:
          return 0;
      }
    });

    setFilteredWorkflows(filtered);
  }, [blockedWorkflows, searchTerm, filterType, sortBy]);

  const handleRefresh = async () => {
    setRefreshing(true);
    await refetch();
    setRefreshing(false);
  };

  const handleOverride = async (workflowId: number, reason: string) => {
    const result = await overridePRBlocking(workflowId, reason);
    if (result.success) {
      // Remove from blocked list
      setBlockedWorkflows((prev) =>
        prev.filter((w) => w.workflow_id !== workflowId)
      );
    }
  };

  const handleEscalate = async (
    workflowId: number,
    level: number,
    reason: string
  ) => {
    const result = await escalatePRIssue(workflowId, level, reason);
    if (result.success) {
      // Update workflow priority
      setBlockedWorkflows((prev) =>
        prev.map((w) =>
          w.workflow_id === workflowId
            ? { ...w, priority: Math.max(w.priority, level) }
            : w
        )
      );
    }
  };

  const getPriorityColor = (priority: number): string => {
    switch (priority) {
      case 4:
        return "text-red-600 bg-red-100";
      case 3:
        return "text-orange-600 bg-orange-100";
      case 2:
        return "text-yellow-600 bg-yellow-100";
      default:
        return "text-blue-600 bg-blue-100";
    }
  };


  const formatDuration = (hours: number): string => {
    if (hours < 1) {
      return `${Math.round(hours * 60)}m`;
    } else if (hours < 24) {
      return `${Math.round(hours * 10) / 10}h`;
    } else {
      const days = Math.floor(hours / 24);
      const remainingHours = Math.round(hours % 24);
      return `${days}d ${remainingHours}h`;
    }
  };

  if (error) {
    return (
      <div
        className={`bg-white rounded-lg shadow-sm border border-red-200 p-6 ${className}`}
      >
        <div className="text-center">
          <AlertTriangle className="h-12 w-12 text-red-500 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">
            Failed to Load Queue Data
          </h3>
          <p className="text-gray-600 mb-4">{error}</p>
          <button
            onClick={handleRefresh}
            className="inline-flex items-center px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors"
          >
            <RefreshCw className="h-4 w-4 mr-2" />
            Retry
          </button>
        </div>
      </div>
    );
  }

  return (
    <div data-testid="pr-blocking-queue" className={`space-y-6 ${className}`}>
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-xl font-bold text-gray-900">PR Blocking Queue</h2>
          <p className="text-gray-600">Workflows blocked by PR conditions</p>
        </div>
        <button
          onClick={handleRefresh}
          disabled={refreshing || loading}
          className="inline-flex items-center px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
        >
          <RefreshCw
            className={`h-4 w-4 mr-2 ${refreshing ? "animate-spin" : ""}`}
          />
          Refresh
        </button>
      </div>

      {/* Analytics Cards */}
      {analytics && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          <MetricCard
            title="Total Blocked"
            value={analytics.total_blocked}
            icon={Clock}
            loading={loading}
          />
          <MetricCard
            title="Escalated"
            value={analytics.escalated_count}
            icon={AlertTriangle}
            loading={loading}
          />
          <MetricCard
            title="Avg Block Time"
            value={`${analytics.average_block_time_hours.toFixed(1)}h`}
            icon={BarChart3}
            loading={loading}
          />
          <MetricCard
            title="Oldest Block"
            value={
              analytics.oldest_block
                ? formatDuration(analytics.oldest_block.duration_hours)
                : "None"
            }
            icon={TrendingUp}
            loading={loading}
          />
        </div>
      )}

      {/* Filters and Search */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
        <div className="flex flex-col sm:flex-row gap-4">
          {/* Search */}
          <div className="flex-1">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
              <input
                type="text"
                placeholder="Search workflows or reasons..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10 pr-4 py-2 w-full border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>
          </div>

          {/* Type Filter */}
          <div className="flex items-center space-x-2">
            <Filter className="h-4 w-4 text-gray-400" />
            <select
              value={filterType}
              onChange={(e) => setFilterType(e.target.value)}
              className="px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            >
              <option value="all">All Types</option>
              <option value="pr_status">PR Status</option>
              <option value="dependency">Dependency</option>
              <option value="resource">Resource</option>
              <option value="manual">Manual</option>
            </select>
          </div>

          {/* Sort */}
          <div className="flex items-center space-x-2">
            <span className="text-sm text-gray-600">Sort by:</span>
            <select
              value={sortBy}
              onChange={(e) =>
                setSortBy(e.target.value as "priority" | "duration" | "type")
              }
              className="px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            >
              <option value="priority">Priority</option>
              <option value="duration">Duration</option>
              <option value="type">Type</option>
            </select>
          </div>
        </div>
      </div>

      {/* Blocked Workflows List */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200">
        {loading ? (
          <div className="p-6 text-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
            <p className="mt-2 text-gray-600">Loading blocked workflows...</p>
          </div>
        ) : filteredWorkflows.length === 0 ? (
          <div className="p-6 text-center">
            <CheckCircle className="h-12 w-12 text-green-500 mx-auto mb-4" />
            <p className="text-gray-600">
              {searchTerm || filterType !== "all"
                ? "No workflows match your filters"
                : "No blocked workflows"}
            </p>
          </div>
        ) : (
          <div className="divide-y divide-gray-200">
            {filteredWorkflows.map((workflow) => (
              <div key={workflow.id} className="p-6 hover:bg-gray-50">
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center space-x-3 mb-2">
                      <GitPullRequest className="h-5 w-5 text-blue-600" />
                      <h3 className="text-lg font-medium text-gray-900">
                        {workflow.workflow_name ||
                          `Workflow ${workflow.workflow_id}`}
                      </h3>
                      <div className="flex items-center space-x-2">
                        <span
                          className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${getPriorityColor(
                            workflow.priority
                          )}`}
                        >
                          Priority {workflow.priority}
                        </span>
                        <StatusBadge
                          status={
                            workflow.blocking_type === "manual"
                              ? "waiting"
                              : workflow.blocking_type === "resource"
                              ? "degraded"
                              : workflow.blocking_type === "pr_status"
                              ? "error"
                              : "unhealthy"
                          }
                        />
                      </div>
                    </div>

                    <p className="text-gray-700 mb-3">{workflow.reason}</p>

                    <div className="flex items-center space-x-6 text-sm text-gray-600">
                      <div className="flex items-center">
                        <Clock className="h-4 w-4 mr-1" />
                        Blocked for {formatDuration(workflow.duration_hours)}
                      </div>
                      {workflow.dependencies &&
                        workflow.dependencies.length > 0 && (
                          <div className="flex items-center">
                            <Users className="h-4 w-4 mr-1" />
                            {workflow.dependencies.length} dependencies
                          </div>
                        )}
                      {workflow.components &&
                        workflow.components.length > 0 && (
                          <div className="flex items-center">
                            <span className="text-gray-400">Components:</span>
                            <span className="ml-1">
                              {workflow.components.join(", ")}
                            </span>
                          </div>
                        )}
                    </div>
                  </div>

                  <div className="flex items-center space-x-2 ml-4">
                    <button
                      onClick={() =>
                        handleOverride(
                          workflow.workflow_id,
                          `Manual override for ${workflow.workflow_name}`
                        )
                      }
                      disabled={actionLoading}
                      className="px-3 py-2 text-sm font-medium text-orange-700 bg-orange-100 border border-orange-300 rounded-md hover:bg-orange-200 disabled:opacity-50"
                    >
                      Override
                    </button>
                    <button
                      onClick={() =>
                        handleEscalate(
                          workflow.workflow_id,
                          workflow.priority + 1,
                          `Escalating due to long block duration: ${formatDuration(
                            workflow.duration_hours
                          )}`
                        )
                      }
                      disabled={actionLoading || workflow.priority >= 4}
                      className="px-3 py-2 text-sm font-medium text-red-700 bg-red-100 border border-red-300 rounded-md hover:bg-red-200 disabled:opacity-50"
                    >
                      Escalate
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Queue Statistics */}
      {analytics && Object.keys(analytics.by_type).length > 0 && (
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <h3 className="text-lg font-medium text-gray-900 mb-4">
            Blocking Types Distribution
          </h3>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {Object.entries(analytics.by_type).map(([type, count]) => (
              <div key={type} className="text-center">
                <div className="text-2xl font-bold text-gray-900">{count}</div>
                <div className="text-sm text-gray-600 capitalize">
                  {type.replace("_", " ")}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};
